import Dexie, { type EntityTable } from 'dexie';
import {
  ENTITY_TYPES,
  defaultSettings,
  emptySnapshot,
  type AnyEntity,
  type EntityMap,
  type EntityType,
  type LedgerSnapshot,
  type MutationGroup,
  type SyncOperation
} from '@kemicontrol/domain';

export type SyncStatus = 'saved-local' | 'offline' | 'syncing' | 'synced' | 'conflict' | 'error';

export interface StoredEntity {
  key: string;
  userId: string;
  type: EntityType;
  record: AnyEntity;
}

export interface StoredMutationGroup extends MutationGroup {
  userId: string;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

export interface StoredConflict {
  id: string;
  userId: string;
  mutationId: string;
  entityType: EntityType;
  entityId: string;
  localRecord?: AnyEntity | undefined;
  remoteRecord: AnyEntity;
  createdAt: string;
}

export interface StoredSyncState {
  userId: string;
  lastRevision: number;
  status: SyncStatus;
  lastSyncAt?: string | undefined;
  error?: string | undefined;
}

export interface SafetySnapshot {
  id: string;
  userId: string;
  createdAt: string;
  reason: string;
  snapshot: LedgerSnapshot;
}

export interface DevicePreference {
  key: string;
  value: unknown;
}

class KemiDatabase extends Dexie {
  entities!: EntityTable<StoredEntity, 'key'>;
  outbox!: EntityTable<StoredMutationGroup, 'mutationId'>;
  conflicts!: EntityTable<StoredConflict, 'id'>;
  syncState!: EntityTable<StoredSyncState, 'userId'>;
  safetySnapshots!: EntityTable<SafetySnapshot, 'id'>;
  devicePreferences!: EntityTable<DevicePreference, 'key'>;

  constructor() {
    super('KemiControl');
    this.version(1).stores({
      entities: '&key, userId, type, [userId+type]',
      outbox: '&mutationId, userId, createdAt, [userId+createdAt]',
      conflicts: '&id, userId, entityType, [userId+entityType]',
      syncState: '&userId',
      safetySnapshots: '&id, userId, createdAt, [userId+createdAt]',
      devicePreferences: '&key'
    });
  }
}

export const db = new KemiDatabase();

export function entityKey(userId: string, type: EntityType, id: string): string {
  return `${userId}:${type}:${id}`;
}

export async function listEntities<T extends EntityType>(userId: string, type: T): Promise<EntityMap[T][]> {
  const values = await db.entities.where('[userId+type]').equals([userId, type]).toArray();
  return values.map((value) => value.record as EntityMap[T]);
}

export async function getEntity<T extends EntityType>(userId: string, type: T, id: string): Promise<EntityMap[T] | undefined> {
  const value = await db.entities.get(entityKey(userId, type, id));
  return value?.record as EntityMap[T] | undefined;
}

export async function snapshotForUser(userId: string): Promise<LedgerSnapshot> {
  const groups = await Promise.all(ENTITY_TYPES.map((type) => listEntities(userId, type)));
  const byType = Object.fromEntries(ENTITY_TYPES.map((type, index) => [type, groups[index] ?? []])) as { [K in EntityType]: EntityMap[K][] };
  const settings = byType.appSettings[0] ?? defaultSettings();
  return {
    accounts: byType.account,
    incomeSources: byType.incomeSource,
    categories: byType.category,
    incomes: byType.income,
    expenses: byType.expense,
    payments: byType.payment,
    transfers: byType.transfer,
    adjustments: byType.adjustment,
    recurrenceRules: byType.recurrenceRule,
    recurrenceExceptions: byType.recurrenceException,
    settings
  };
}

export async function replaceSnapshot(userId: string, snapshot: LedgerSnapshot): Promise<void> {
  const values: StoredEntity[] = [];
  const add = <T extends EntityType>(type: T, records: EntityMap[T][]) => {
    for (const record of records) values.push({ key: entityKey(userId, type, record.id), userId, type, record });
  };
  add('account', snapshot.accounts);
  add('incomeSource', snapshot.incomeSources);
  add('category', snapshot.categories);
  add('income', snapshot.incomes);
  add('expense', snapshot.expenses);
  add('payment', snapshot.payments);
  add('transfer', snapshot.transfers);
  add('adjustment', snapshot.adjustments);
  add('recurrenceRule', snapshot.recurrenceRules);
  add('recurrenceException', snapshot.recurrenceExceptions);
  add('appSettings', [snapshot.settings]);
  await db.transaction('rw', db.entities, async () => {
    await db.entities.where('userId').equals(userId).delete();
    await db.entities.bulkPut(values);
  });
}

export async function purgeUserData(userId: string): Promise<void> {
  await db.transaction('rw', db.entities, db.outbox, db.conflicts, db.syncState, db.safetySnapshots, async () => {
    await Promise.all([
      db.entities.where('userId').equals(userId).delete(),
      db.outbox.where('userId').equals(userId).delete(),
      db.conflicts.where('userId').equals(userId).delete(),
      db.syncState.delete(userId),
      db.safetySnapshots.where('userId').equals(userId).delete()
    ]);
  });
}

export async function ensureSettings(userId: string): Promise<void> {
  const settings = await listEntities(userId, 'appSettings');
  if (!settings.length) {
    const record = defaultSettings();
    await db.entities.put({ key: entityKey(userId, 'appSettings', record.id), userId, type: 'appSettings', record });
  }
  await db.syncState.put((await db.syncState.get(userId)) ?? { userId, lastRevision: 0, status: 'saved-local' });
}

export function asLocalOperation(operation: SyncOperation): SyncOperation {
  return operation;
}

export function blankSnapshot(): LedgerSnapshot { return emptySnapshot(); }
